import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Divider, Space, Typography } from 'antd';
import { UpOutlined } from '@ant-design/icons';
import {
  faBuilding,
  faBus,
  faClipboardList,
  faClock,
  faGlobe,
  faHome,
  faMapMarkerAlt,
  faRulerHorizontal,
  faShoppingBag,
  faStopwatch,
  faStreetView,
  faTimes,
  faUsers,
  faVenusMars,
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { useDataFilter } from '../../../hooks/SimViewer/useDataFilter';
import { useFeatureOptionsData } from '../../../hooks/data/useFeatureOptionsData';
import { scrollbarStyle } from '../../../styles/scrollbar';
import { InfoPopoverButton } from '../../InfoPopoverButton';
import { SideMenuFilter } from '../SideMenuFilter/SideMenuFilter';
import { SideMenuSummary } from './SideMenuSummary';
import { FeatureIcon } from './FeatureIcon';
import { AttributePanel } from './AttributePanel';
import { AreaRankingPanel } from './AreaRankingPanel';
import { HistogramPanel } from './HistogramPanel';
import { StayingPopulationPanel } from './StayingPopulationPanel';
import { StopByPanel } from './StopByPanel';
import { TripPathPanel } from './TripPathPanel';
import { TrafficVolumePanel } from './TrafficVolumePanel';
import { PassageTimePanel } from './PassageTimePanel';
import { OdMapPanel } from './OdMapPanel';
import { CrossAnalysisPanel } from './CrossAnalysisPanel';
import { SideMenuOverlay } from './SideMenuOverlay';

type Props = {
  open: boolean;
};

const SimViewerSideMenu = ({ open }: Props): ReactNode => {
  const { t } = useTranslation();
  const { agentFilterCount, tripFilterCount, advancedFilterCount } =
    useDataFilter();
  const { featureOptions } = useFeatureOptionsData();

  const isFiltered =
    agentFilterCount + tripFilterCount + advancedFilterCount > 0;

  return (
    <SimViewerDrawer open={open}>
      {!isFiltered && <SideMenuOverlay />}
      <Space
        style={{
          width: '100%',
          justifyContent: 'space-between',
          padding: '16px 16px 8px',
          zIndex: 2,
        }}
      >
        <SideMenuSummary />
      </Space>
      <SideMenuFilter />
      <Divider style={{ margin: 0, borderTop: '3px solid #e1e1e1' }} />
      <ScrollableArea>
        <Collapse
          ghost
          expandIconPosition="right"
          expandIcon={({ isActive }) => (
            <UpOutlined rotate={isActive ? 0 : 180} />
          )}
          destroyInactivePanel
        >
          {/* 人の分析 */}
          <SectionTitle>{t('agentAnalysis')}</SectionTitle>
          {featureOptions?.use_gender && (
            <AttributePanel
              key="gender"
              dimension="gender"
              icon={<FeatureIcon icon={faVenusMars} />}
              label={t('gender')}
              extra={
                <InfoPopoverButton content={t('additionalInfos.gender')} />
              }
            />
          )}
          {featureOptions?.use_age && (
            <AttributePanel
              key="age"
              dimension="age"
              icon={<FeatureIcon icon={faClipboardList} />}
              label={t('ageGroup')}
              extra={
                <InfoPopoverButton content={t('additionalInfos.ageGroup')} />
              }
            />
          )}
          {featureOptions?.use_nationality && (
            <AttributePanel
              key="nationality"
              dimension="nationality"
              icon={<FeatureIcon icon={faGlobe} />}
              label={t('nationality')}
            />
          )}
          {featureOptions?.use_home_agents && (
            <AreaRankingPanel
              key="home"
              type="home"
              icon={<FeatureIcon icon={faHome} />}
              label={t('home')}
              extra={<InfoPopoverButton content={t('additionalInfos.home')} />}
            />
          )}
          {featureOptions?.use_work_agents && (
            <AreaRankingPanel
              key="work"
              type="work"
              icon={<FeatureIcon icon={faBuilding} />}
              label={t('work')}
              extra={<InfoPopoverButton content={t('additionalInfos.work')} />}
            />
          )}

          {/* 移動の分析 */}
          <Divider style={{ margin: 0 }} />
          <SectionTitle>{t('tripAnalysis')}</SectionTitle>
          {featureOptions?.use_end_activity_type && (
            <AttributePanel
              key="end_activity_type"
              dimension="end_activity_type"
              icon={<FeatureIcon icon={faShoppingBag} />}
              label={t('endActivity')}
              extra={
                <InfoPopoverButton
                  content={t('additionalInfos.endActivityType')}
                />
              }
            />
          )}
          {featureOptions?.use_longest_distance_mode && (
            <AttributePanel
              key="longest_distance_mode"
              dimension="longest_distance_mode"
              icon={<FeatureIcon icon={faBus} />}
              label={t('longestDistanceMode')}
              extra={
                <InfoPopoverButton
                  content={t('additionalInfos.longestDistanceMode')}
                />
              }
            />
          )}
          {featureOptions?.use_traveled_time && (
            <HistogramPanel
              key="traveled_time"
              type="traveled_time"
              icon={<FeatureIcon icon={faStopwatch} />}
              label={t('traveledTime')}
            />
          )}
          {featureOptions?.use_traveled_distance && (
            <HistogramPanel
              key="traveled_distance"
              type="traveled_distance"
              icon={<FeatureIcon icon={faRulerHorizontal} />}
              label={t('traveledDistance')}
            />
          )}
          <Divider style={{ margin: '0 0 0 48px' }} />
          {featureOptions?.use_dep_time && (
            <HistogramPanel
              key="dep_time"
              type="dep_time"
              icon={<FeatureIcon icon={faClock} />}
              label={t('departureTime')}
            />
          )}
          {featureOptions?.use_arr_time && (
            <HistogramPanel
              key="arr_time"
              type="arr_time"
              icon={<FeatureIcon icon={faClock} />}
              label={t('arrivalTime')}
            />
          )}
          {featureOptions?.use_start_trips && (
            <AreaRankingPanel
              key="start"
              type="start"
              icon={<FeatureIcon icon={faMapMarkerAlt} />}
              label={t('tripStart')}
            />
          )}
          {featureOptions?.use_end_trips && (
            <AreaRankingPanel
              key="end"
              type="end"
              icon={<FeatureIcon icon={faMapMarkerAlt} />}
              label={t('tripEnd')}
            />
          )}
          <Divider style={{ margin: '0 0 0 48px' }} />
          {featureOptions?.use_activity_time && (
            <HistogramPanel
              key="activity_time"
              type="activity_time"
              icon={<FeatureIcon icon={faStopwatch} />}
              label={t('activityTime')}
              extra={
                <InfoPopoverButton
                  content={t('additionalInfos.activityTime')}
                />
              }
            />
          )}
          {featureOptions?.use_staying_population && (
            <StayingPopulationPanel
              key="staying_population"
              icon={<FeatureIcon icon={faUsers} />}
              label={t('stayingPopulation')}
            />
          )}
          {featureOptions?.use_passed_routes_view && (
            <PassageTimePanel
              key="passage_time"
              icon={<FeatureIcon icon={faClock} />}
              label={t('passageTime')}
              extra={
                <InfoPopoverButton content={t('additionalInfos.passageTime')} />
              }
            />
          )}
          {/* 高度な分析 */}
          <Divider style={{ margin: 0 }} />
          <SectionTitle>{t('advancedAnalysis')}</SectionTitle>
          {featureOptions?.use_trips_view && (
            <TripPathPanel
              key="trip_path"
              icon={<FeatureIcon icon={faMapMarkerAlt} />}
              label={t('tripPath')}
              extra={
                <InfoPopoverButton content={t('additionalInfos.tripPath')} />
              }
            />
          )}
          {featureOptions?.use_passed_routes_view && (
            <TrafficVolumePanel
              key="traffic_volume"
              icon={<FeatureIcon icon={faMapMarkerAlt} />}
              label={t('traffic')}
              extra={
                <InfoPopoverButton
                  content={t('additionalInfos.trafficVolume')}
                />
              }
            />
          )}
          {featureOptions?.use_od_maps_view && (
            <OdMapPanel
              key="od_map"
              icon={<FeatureIcon icon={faMapMarkerAlt} />}
              label={t('odMap')}
              extra={<InfoPopoverButton content={t('additionalInfos.odMap')} />}
            />
          )}
          {featureOptions?.use_step_by_view && (
            <StopByPanel
              key="stop_by"
              icon={<FeatureIcon icon={faStreetView} />}
              label={t('stopBy')}
            />
          )}
          <CrossAnalysisPanel
            key="cross_analysis"
            icon={<FeatureIcon icon={faTimes} />}
            label={t('crossAnalysis')}
          />
        </Collapse>
      </ScrollableArea>
    </SimViewerDrawer>
  );
};

const ScrollableArea = styled.div`
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  overscroll-behavior: none;
  ${scrollbarStyle}
`;

const SectionTitle = styled(Typography.Text)`
  position: sticky;
  top: 0;
  z-index: 1;
  display: block;
  padding: 16px 16px 8px 8px;
  font-weight: 600;
  color: #8c8c8c;
  border: none;
`;
SectionTitle.displayName = 'SectionTitle';

const SimViewerDrawer = styled.div<{ open: boolean }>`
  position: absolute;
  display: flex;
  flex-direction: column;
  left: 0;
  top: 0;
  width: clamp(360px, 25vw, 421px);
  height: 100%;
  transform: translateX(
    ${({ open }) => (open ? 0 : 'calc(-1 * clamp(360px, 25vw, 421px))')}
  );
  z-index: 1000;
  transition: transform 0.24s ease-in-out;
  background-color: #f9f9f9;
`;
SimViewerDrawer.displayName = 'SimViewerDrawer';

export { SimViewerSideMenu };
